import React, { useState, useEffect } from 'react';
import { Check, Minus, XCircle, Trash2, Edit, MoreVertical } from 'lucide-react';
import { Charge, PricingMetric } from './types/planTypes';
import { formatPrice, getChargeTypeDisplay } from './utils/planDisplayUtils';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectPricingMetricName, selectPricingMetricModality } from '@/store/selectors/metricsSelectors';
import { getModalityIcon, getModalityColor } from '@/components/companies/MetricsTable';
import EditChargeModal from './EditChargeModal';
import DeleteChargeModal from './DeleteChargeModal';
import { useToast } from '@/hooks/use-toast';
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ChargeRowProps {
  charge: Charge;
  planId: string;
  planName: string;
  quarterlyVersions: string[];
  isChargeInVersion: (planId: string, chargeId: string, version: string) => boolean;
  isScreenshotInvalid: (version: string) => boolean;
  openImageModal: (version: string, title: string) => void;
  isLegacyPlan: (planId: string) => boolean;
  showCurrentOnly: boolean;
  openChargeMappingDialog?: (charge: Charge, planId: string, planName: string, version: string) => void;
  onChargeDeleted?: () => void;
  // All available scrapes for finding the true latest version
  allScrapes?: Array<{version: string, quarter: string}> | null;
}

const ChargeRow: React.FC<ChargeRowProps> = ({
  charge,
  planId,
  planName,
  quarterlyVersions,
  isChargeInVersion,
  isScreenshotInvalid,
  openImageModal,
  isLegacyPlan,
  showCurrentOnly,
  openChargeMappingDialog,
  onChargeDeleted,
  allScrapes
}) => {
  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Used to force re-render
  const { toast } = useToast();

  // Calculate the latest version once for reuse
  // If allScrapes is available, use that for finding the latest version
  const latestVersion = allScrapes && allScrapes.length > 0
    ? [...allScrapes].sort((a, b) => parseInt(b.version) - parseInt(a.version))[0].version
    : quarterlyVersions.length > 0
      ? [...quarterlyVersions].sort((a, b) => parseInt(b) - parseInt(a))[0]
      : null;

  // Listen for charge mapping updates via custom event
  useEffect(() => {
    // Handler for the custom event
    const handleChargeMappingUpdate = (e: CustomEvent) => {
      // Check if this is the charge that was updated
      if (e.detail && e.detail.chargeId === charge.id) {
        console.log(`🔄 [ChargeRow] Detected mapping update for charge ${charge.id}`);
        // Force component to re-render by updating the refresh key
        setRefreshKey(prevKey => prevKey + 1);
      }
    };
    
    // Add event listener
    window.addEventListener('charge-mapping-updated', handleChargeMappingUpdate as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('charge-mapping-updated', handleChargeMappingUpdate as EventListener);
    };
  }, [charge.id]); // Only re-run if charge ID changes

  // Get pricing metric data - first check if it's embedded in the charge from GraphQL, then fall back to Redux store
  const metricNameFromRedux = useAppSelector(state => selectPricingMetricName(state, charge.pricing_metric_id));
  const metricModalityFromRedux = useAppSelector(state => selectPricingMetricModality(state, charge.pricing_metric_id));
  
  const metricName = charge.pricing_metric?.display_name || metricNameFromRedux;
  const metricModality = charge.pricing_metric?.modality || metricModalityFromRedux;
  
  // Special handling for empty state
  if (charge.type === 'empty') {
    return (
      <TableRow className="border-t-0 hover:bg-gray-100">
        <TableCell className="pl-8 text-sm text-gray-400 py-2">
          <div className="flex justify-between items-center">
            <span className="italic">No pricing data available</span>
          </div>
        </TableCell>
        <TableCell className="text-sm text-gray-400">
          <span className="text-gray-300">—</span>
        </TableCell>
        <TableCell className="text-sm text-gray-400">
          <span className="text-gray-300">—</span>
        </TableCell>

        {/* Latest column - empty for empty state */}
        <TableCell className="text-center">
          <span className="text-gray-300">—</span>
        </TableCell>

        {/* Version columns */}
        {quarterlyVersions.map(version => (
          <TableCell
            key={version}
            className="text-center cursor-pointer hover:bg-blue-50"
            onClick={() => openImageModal(version, planName)}
          >
            <div className="flex justify-center">
              <span className="text-gray-300">—</span>
            </div>
          </TableCell>
        ))}
        
        {/* Empty cell for Actions column */}
        <TableCell className="text-center">
          <span className="text-gray-300">—</span>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow
      className={`border-t-0 hover:bg-gray-100 ${!showCurrentOnly && planId && isLegacyPlan(planId) ? "bg-blue-50" : ""}`}
    >
      <TableCell
        className="pl-8 text-sm text-gray-600 py-2 cursor-pointer"
        onClick={() => setIsEditModalOpen(true)}
      >
        <div className="flex justify-between items-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="w-full">
                 <div className="flex justify-between items-center truncate">
                   <div className="flex items-center space-x-1 truncate">
                     <span className="font-medium truncate">{formatPrice(charge)}</span>
                     {charge.per && <span className="text-xs text-gray-500 whitespace-nowrap">/{charge.per}</span>}
                   </div>
                 </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-medium">{getChargeTypeDisplay(charge.type)}</p>
                  <p>
                    <span className="font-medium">Full price:</span> 
                    {charge.currency === '%' ? 
                      <>{charge.price}{charge.currency}</> : 
                      <>{charge.currency || '$'}{charge.price}</>
                    }
                    {charge.price_per ? ` ${charge.price_per}` : ''}
                    {charge.per ? ` per ${charge.per}` : ''}
                  </p>
                  {charge.notes && charge.notes !== 'null' && (
                    <p className="text-xs text-gray-600 mt-1 italic">{charge.notes}</p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
      
      {/* Pricing Metric column */}
      <TableCell
        className="text-sm text-gray-600 cursor-pointer"
        onClick={() => setIsEditModalOpen(true)}
      >
        {metricName ? (
          <div className="flex items-center space-x-1.5">
            <div className={`flex items-center justify-center p-1 rounded-full ${getModalityColor(metricModality || 'unknown')}`}>
              {getModalityIcon(metricModality || 'unknown')}
            </div>
            <div className="flex flex-col truncate">
              <span className="text-xs font-medium truncate">{metricName}</span>
              {metricModality && (
                <span className="text-xs text-gray-500 truncate">{metricModality}</span>
              )}
            </div>
          </div>
        ) : charge.pricing_metric?.id || charge.pricing_metric_id ? (
          <span className="text-xs text-gray-400">{charge.pricing_metric?.id || charge.pricing_metric_id}</span>
        ) : (
          <div className="flex items-center space-x-1.5">
            <div className={`flex items-center justify-center p-1 rounded-full ${getModalityColor('flat')}`}>
              {getModalityIcon('flat')}
            </div>
            <div className="flex flex-col truncate">
              <span className="text-xs font-medium truncate">Flat Rate</span>
              <span className="text-xs text-gray-500 truncate">Flat</span>
            </div>
          </div>
        )}
      </TableCell>
      
      <TableCell
        className="text-sm text-gray-600 cursor-pointer"
        onClick={() => setIsEditModalOpen(true)}
      >
        <span className="text-xs font-medium">
          {getChargeTypeDisplay(charge.type)}
        </span>
      </TableCell>
      
      {/* Latest column */}
      <TableCell 
        className="text-center cursor-pointer hover:bg-blue-50"
        onClick={() => {
          // Show screenshot if available
          if (latestVersion) {
            openImageModal(latestVersion, `${planName} - ${charge.type ? getChargeTypeDisplay(charge.type) : 'Charge'} (Latest)`);
          }
        }}
        onContextMenu={(e) => {
          // Right-click - show mapping dialog
          e.preventDefault();
          if (latestVersion && openChargeMappingDialog && planId && planName) {
            openChargeMappingDialog(charge, planId, planName, latestVersion);
          }
        }}
      >
        {latestVersion && isChargeInVersion(planId, charge.id, latestVersion) ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  {isScreenshotInvalid(latestVersion) ? (
                    <XCircle className="h-4 w-4 text-red-600" />
                  ) : (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {isScreenshotInvalid(latestVersion) ? 
                  "Invalid screenshot - click to view" : 
                  "Charge exists in latest version - click to view screenshot"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  <Minus className="h-4 w-4 text-gray-300" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                Charge does not exist in latest version
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </TableCell>
      
      {/* Quarterly version columns */}
      {quarterlyVersions.map(version => (
        <TableCell
          key={version}
          className="text-center cursor-pointer hover:bg-blue-50"
          onClick={(e) => {
            // If right-click or ctrl+click, show mapping dialog
            if (e.ctrlKey || e.button === 2 || e.altKey) {
              e.preventDefault();
              e.stopPropagation();
              if (openChargeMappingDialog && planId && planName) {
                openChargeMappingDialog(charge, planId, planName, version);
              }
            } else {
              // Normal click - show image modal
              openImageModal(version, `${planName} - ${getChargeTypeDisplay(charge.type)}`);
            }
          }}
          onContextMenu={(e) => {
            // Right-click - show mapping dialog
            e.preventDefault();
            if (openChargeMappingDialog && planId && planName) {
              openChargeMappingDialog(charge, planId, planName, version);
            }
          }}
        >
          {isChargeInVersion(planId, charge.id, version) ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-center">
                    {isScreenshotInvalid(version) ? (
                      <XCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {isScreenshotInvalid(version) ? 
                    "Invalid screenshot - click to view" : 
                    "Charge exists in this version - click to view screenshot, right-click to edit mapping"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-center">
                    <Minus className="h-4 w-4 text-gray-300" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  Charge does not exist in this version - right-click to edit mapping
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </TableCell>
      ))}
      
      {/* Actions column with dropdown menu */}
      <TableCell className="text-center">
        <div className="flex justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-gray-400 hover:text-gray-600"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="focus:bg-blue-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditModalOpen(true);
                }}
              >
                <Edit className="h-4 w-4 mr-2 text-blue-600" />
                <span>Edit Charge</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteModalOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                <span>Delete Charge</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Edit Modal */}
        <EditChargeModal
          charge={charge}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={(updatedCharge) => {
            // Local state update - the actual DB update is handled in the modal
            setIsEditModalOpen(false);
          }}
        />
        
        {/* Delete Charge Modal */}
        <DeleteChargeModal
          isOpen={isDeleteModalOpen}
          onOpenChange={setIsDeleteModalOpen}
          chargeId={charge.id}
          chargeName={formatPrice(charge)}
          onSuccess={() => {
            // Notify parent component that charge was deleted
            if (onChargeDeleted) {
              onChargeDeleted();
            }
          }}
        />
      </TableCell>
    </TableRow>
  );
};

export default ChargeRow;
